'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Plus, X, Users } from 'lucide-react';
import { createCommittee } from '@/af_actions/declarationActions';
import { useRouter } from 'next/navigation';
import { ShowIfRole } from '@/components/ShowIfRole';

interface Committee {
  id: string;
  name: string;
  slug: string;
  description?: string;
  created_by: string;
  created_at: string;
  member_count: number;
}

export default function CommitteeListClient({ committees, userId }: { committees: Committee[], userId: string | null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) {
      alert("You must be signed in to create a committee.");
      return;
    }
    setLoading(true);
    try {
      await createCommittee(name, description);
      setName('');
      setDescription('');
      setIsOpen(false);
      router.refresh();
    } catch (error) {
      console.error(error);
      alert('Failed to create committee');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="w-full min-h-screen text-black bg-amber-50">
      <div className="max-w-4xl mx-auto p-8">
        <div className="mb-12">
          <h1 className="text-4xl font-bold font-serif mb-2 text-slate-900">Committees</h1>
          <p className="text-slate-600 font-serif">Committees to organize political discussions</p>
        </div>

        {!isOpen && (
          <ShowIfRole>
          <button
            onClick={() => {
              if (!userId) {
                alert("You must be signed in to create a committee.");
                return;
              }
              setIsOpen(true);
            }}
            className="w-full mb-8 py-4 border-2 border-dashed border-amber-300 rounded-lg text-amber-800 hover:bg-amber-100/50 hover:border-amber-400 transition-all flex items-center justify-center gap-2 font-serif font-bold uppercase tracking-widest text-sm"
          >
            <Plus size={20} />
            Create New Committee
          </button>
          </ShowIfRole>
        )}

        {isOpen && (
          <div className="bg-white text-black border-2 border-amber-500 p-6 rounded-lg mb-8 shadow-lg animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-amber-900 font-serif">New Committee</h2>
              <button
                onClick={() => setIsOpen(false)}
                className="text-amber-500 hover:text-amber-700 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-amber-800 mb-1 font-sans">Committee Name</label>
                <input
                  required
                  placeholder="e.g., Infrastructure, Education, Health"
                  className="w-full p-3 border border-amber-200 rounded focus:outline-none focus:ring-2 focus:ring-amber-500 bg-amber-50/30 font-serif text-lg"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-amber-800 mb-1 font-sans">Description</label>
                <textarea
                  placeholder="What is this committee about?"
                  rows={3}
                  className="w-full p-3 border border-amber-200 rounded focus:outline-none focus:ring-2 focus:ring-amber-500 bg-amber-50/30 font-serif text-sm"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-amber-800 text-white px-6 py-3 rounded font-bold uppercase tracking-widest text-sm hover:bg-amber-900 disabled:opacity-50 transition-colors shadow-md"
                >
                  {loading ? 'Creating...' : 'Create Committee'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-6 py-3 border border-amber-200 text-amber-800 rounded font-bold uppercase tracking-widest text-sm hover:bg-amber-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid gap-6">
          {committees.map((committee) => (
            <Link
              key={committee.id}
              href={`/assembly/committee/${committee.slug}`}
              className="block group"
            >
              <article className="border border-black p-6 bg-white/50 rounded-sm transition-all group-hover:bg-gray-100 hover:border-amber-500 hover:shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <h2 className="text-2xl font-bold text-slate-900 font-serif group-hover:text-blue-800 transition-colors">
                    {committee.name}
                  </h2>
                  {/*
                  <div className="flex items-center gap-1 text-slate-500 font-sans text-xs">
                    <Users size={14} />
                    <span className="font-semibold">{committee.member_count || 0}</span>
                  </div>*/}
                </div>

                {committee.description && (
                  <p className="text-slate-600 font-serif text-sm mb-4 line-clamp-2">
                    {committee.description}
                  </p>
                )}

                <div className="flex items-center justify-between text-xs font-sans text-slate-500 uppercase tracking-widest">
                  <span>
                    Created {new Date(committee.created_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                  </span>
                  <span className="text-amber-700 font-semibold group-hover:underline">
                    View Resolutions →
                  </span>
                </div>
              </article>
            </Link>
          ))}

          {committees.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500 font-serif mb-4">No committees yet. Be the first to create one!</p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
